import type { RefObject } from 'react';
import { DndContext, closestCenter, type DragEndEvent, type SensorDescriptor, type SensorOptions } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Images } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { UserItem } from '@/types/userItem';
import { SortablePhotoRow } from './SortablePhotoRow';

type ReorderImages = NonNullable<UserItem['images']>;

interface ProofError {
  message: string;
  missing?: string[];
  notes?: string;
}

interface ManagePhotosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMobile: boolean;
  proofError: ProofError | null;
  dndSensors: SensorDescriptor<SensorOptions>[];
  reorderDraft: ReorderImages;
  setReorderDraft: React.Dispatch<React.SetStateAction<ReorderImages>>;
  existingProof: Record<string, string>;
  verifyingProofUrl: string | null;
  isVerifyingProof: boolean;
  isSavingProof: boolean;
  proofEligible: boolean;
  isSavingReorder: boolean;
  onClearProof: () => void;
  onVerifyProof: (url: string) => void;
  onRequestDelete: (url: string) => void;
  onCancel: () => void;
  onSave: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  proofCameraInputRef: RefObject<HTMLInputElement>;
  proofGalleryInputRef: RefObject<HTMLInputElement>;
  handleProofPhotoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function ManagePhotosDialog({
  open,
  onOpenChange,
  isMobile,
  proofError,
  dndSensors,
  reorderDraft,
  setReorderDraft,
  existingProof,
  verifyingProofUrl,
  isVerifyingProof,
  isSavingProof,
  proofEligible,
  isSavingReorder,
  onClearProof,
  onVerifyProof,
  onRequestDelete,
  onCancel,
  onSave,
  fileInputRef,
  proofCameraInputRef,
  proofGalleryInputRef,
  handleProofPhotoUpload,
}: ManagePhotosDialogProps) {
  const body = (
    <>
      <p className="text-[13px] text-white/50 leading-snug mb-3">
        Drag to reorder. First photo is your cover. Tap the shield to mark a verified proof photo.
      </p>

      {proofError && (
        <div className="mt-1 mb-3 rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <div className="font-medium mb-1">{proofError.message}</div>
          {proofError.missing && (
            <ul className="list-disc list-inside space-y-0.5 text-destructive/90">
              {proofError.missing.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          )}
          {proofError.notes && (
            <div className="mt-2 pt-2 border-t border-destructive/20 text-[11px] text-destructive/80 leading-snug">
              {proofError.notes}
            </div>
          )}
        </div>
      )}

      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={(e: DragEndEvent) => {
          const { active, over } = e;
          if (!over || active.id === over.id) return;
          setReorderDraft((items) => {
            const oldIndex = items.findIndex((img) => img.id === active.id);
            const newIndex = items.findIndex((img) => img.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return items;
            return arrayMove(items, oldIndex, newIndex);
          });
        }}
      >
        <SortableContext items={reorderDraft.map((img) => img.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {reorderDraft.map((img) => {
              const url = img.image_url;
              const isProof = existingProof.possession === url;
              const isCover = reorderDraft[0]?.id === img.id;
              const isVerifyingThis = verifyingProofUrl === url;
              const rowBusy = isVerifyingProof || isSavingProof;
              return (
                <SortablePhotoRow
                  key={img.id}
                  id={img.id}
                  url={url}
                  isCover={isCover}
                  isProof={isProof}
                  isVerifying={isVerifyingThis}
                  rowBusy={rowBusy}
                  proofEligible={proofEligible}
                  onToggleProof={() => (isProof ? onClearProof() : onVerifyProof(url))}
                  onDelete={() => onRequestDelete(url)}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {reorderDraft.length === 0 && (
        <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-8 text-center text-sm text-white/60">
          No photos yet. Add some to enable proof pack and reordering.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-5">
        <Button
          type="button"
          variant="outline"
          className="bg-white/[0.05] border-white/[0.08]"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSavingReorder}
        >
          <Images className="w-4 h-4 mr-2" />
          Add photos
        </Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" className="text-white/60 hover:text-white" onClick={onCancel} disabled={isSavingReorder}>
            Cancel
          </Button>
          <Button type="button" onClick={onSave} disabled={isSavingReorder || reorderDraft.length === 0} className="rounded-full px-6">
            {isSavingReorder ? 'Saving…' : 'Save order'}
          </Button>
        </div>
      </div>
      <input ref={proofCameraInputRef} type="file" accept="image/*,.heic,.heif,.dng" capture="environment" className="hidden" onChange={handleProofPhotoUpload} />
      <input ref={proofGalleryInputRef} type="file" accept="image/*,.heic,.heif,.dng" className="hidden" onChange={handleProofPhotoUpload} />
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="bg-chrome border-white/[0.08] rounded-t-3xl max-h-[85dvh] overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Manage Photos</SheetTitle>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg sm:max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Photos</DialogTitle>
          <DialogDescription className="sr-only">
            Reorder photos, mark a verified proof photo, or add more.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
